/**
 * FaceVerification Component
 *
 * Authenticates user using their enrolled face.
 * Performs challenge-response authentication with client-side signing.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Heading,
  Alert,
  AlertIcon,
  AlertDescription,
  Progress,
  Spinner,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client';

import { FaceCapture } from './FaceCapture';
import { reproduceDeterministicFromFace, reproduceFromFace } from '../lib/fuzzyExtractor';
import { deriveKeyPair, signChallenge } from '../lib/keyDerivation';
import { ZK_REQUEST_CHALLENGE, ZK_VERIFY_AUTH, ZK_GET_HELPER_DATA, ZK_GET_SALT } from '../queries';
import type { FaceVerificationProps } from '../types';

// Local storage key for stored credentials
const ZK_CREDENTIALS_KEY = 'zk_credentials';

// Number of face samples to capture during authentication for consistency
const AUTH_SAMPLE_COUNT = 3;

// Number of failed attempts before suggesting re-enrollment
const MAX_ATTEMPTS_BEFORE_REENROLL = 2;

// ============================================================================
// Types
// ============================================================================

type VerificationStep = 'loading' | 'capture' | 'processing' | 'success' | 'error';

// ============================================================================
// Component
// ============================================================================

export const FaceVerification: React.FC<FaceVerificationProps> = ({
  onSuccess,
  onError,
  onCancel,
}) => {
  // State
  const [step, setStep] = useState<VerificationStep>('capture');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [storedPublicKey, setStoredPublicKey] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Hooks
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // GraphQL
  const [requestChallenge] = useMutation(ZK_REQUEST_CHALLENGE);
  const [verifyAuth] = useMutation(ZK_VERIFY_AUTH);
  const [getHelperData] = useLazyQuery(ZK_GET_HELPER_DATA);
  const { data: saltData } = useQuery(ZK_GET_SALT);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleFaceCapture = useCallback(
    async (descriptor: Float32Array) => {
      setStep('processing');
      setProgress(10);
      setError(null);

      try {
        // Step 1: Load stored credentials (public keys) from localStorage
        console.log('[ZK Auth] Loading stored credentials...');
        let storedCredentials: Array<{ publicKey: string; credentialType: string }> = [];
        try {
          storedCredentials = JSON.parse(localStorage.getItem(ZK_CREDENTIALS_KEY) || '[]');
        } catch {
          storedCredentials = [];
        }
        console.log('[ZK Auth] Found', storedCredentials.length, 'stored credentials');

        if (storedCredentials.length === 0) {
          throw new Error('No enrolled credentials found. Please enroll first.');
        }

        // Step 2: Request challenge from server
        setProgress(20);
        console.log('[ZK Auth] Requesting challenge...');

        const { data: challengeData } = await requestChallenge({
          variables: { publicKey: storedPublicKey },
        });

        if (!challengeData?.zkRequestChallenge?.challenge) {
          throw new Error('Failed to get challenge from server');
        }

        const challenge = challengeData.zkRequestChallenge.challenge;
        console.log('[ZK Auth] Got challenge:', challenge.substring(0, 16) + '...');

        // Step 3: Try to reproduce key using each stored credential
        setProgress(40);
        console.log('[ZK Auth] Attempting key reproduction with stored credentials...');

        let successfulKeyPair: { publicKeyHex: string; privateKey: Uint8Array } | null = null;
        let successfulCredential: { publicKey: string } | null = null;

        for (const credential of storedCredentials) {
          console.log('[ZK Auth] Trying credential:', credential.publicKey.substring(0, 20) + '...');

          try {
            // Fetch helper data for this public key
            const { data: helperDataResult } = await getHelperData({
              variables: { publicKey: credential.publicKey },
            });

            const helperData = helperDataResult?.zkGetHelperData;
            if (!helperData) {
              console.log('[ZK Auth] No helper data found for this credential');
              continue;
            }

            console.log('[ZK Auth] Got helper data, attempting reproduction...');

            // Try to reproduce the key using the helper data
            // Use deterministic method if we have salt, otherwise fall back to legacy
            let reproducedKey: Uint8Array | null = null;
            if (saltData?.zkGetSalt) {
              console.log('[ZK Auth] Using DETERMINISTIC reproduction with server salt');
              reproducedKey = reproduceDeterministicFromFace(descriptor, helperData, saltData.zkGetSalt);
            } else {
              console.log('[ZK Auth] Falling back to legacy reproduction (no salt)');
              reproducedKey = reproduceFromFace(descriptor, helperData);
            }

            if (reproducedKey) {
              console.log('[ZK Auth] Key reproduced successfully!');
              const keyPair = deriveKeyPair(reproducedKey);

              // Verify the public key matches
              console.log('[ZK Auth] Comparing public keys:');
              console.log('[ZK Auth]   Derived:', keyPair.publicKeyHex.substring(0, 30) + '...');
              console.log('[ZK Auth]   Expected:', credential.publicKey.substring(0, 30) + '...');

              if (keyPair.publicKeyHex === credential.publicKey) {
                console.log('[ZK Auth] Public key matches! Using this credential.');
                successfulKeyPair = keyPair;
                successfulCredential = credential;
                break;
              } else {
                console.log('[ZK Auth] Public key mismatch - face descriptors differ too much');
                console.log('[ZK Auth] This is normal if lighting/angle changed significantly');
              }
            } else {
              console.log('[ZK Auth] Key reproduction returned null - should not happen in lenient mode');
            }
          } catch (credErr) {
            console.warn('[ZK Auth] Error with credential:', credErr);
          }
        }

        if (!successfulKeyPair || !successfulCredential) {
          throw new Error('Face not recognized. Try positioning your face exactly as during enrollment, with similar lighting. You may need to re-enroll if conditions changed significantly.');
        }

        // Step 4: Sign the challenge
        setProgress(70);
        console.log('[ZK Auth] Signing challenge...');

        const signature = await signChallenge(challenge, successfulKeyPair.privateKey);

        // Step 5: Send signature to server for verification
        setProgress(85);
        console.log('[ZK Auth] Verifying with server...');

        const { data: authData } = await verifyAuth({
          variables: {
            input: {
              challenge,
              signature,
              publicKey: successfulKeyPair.publicKeyHex,
            },
          },
        });

        setProgress(100);

        if (authData?.zkVerifyAuth?.token) {
          console.log('[ZK Auth] Authentication successful!');
          setStep('success');

          toast({
            title: 'Welcome Back!',
            description: 'Face authentication successful.',
            status: 'success',
            duration: 3000,
          });

          // Return the token
          setTimeout(() => {
            onSuccess(authData.zkVerifyAuth.token);
          }, 1000);
        } else {
          throw new Error('Authentication failed');
        }
      } catch (err) {
        console.error('[ZK Auth] Authentication failed:', err);
        const errorMsg = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMsg);
        setStep('error');
        setFailedAttempts(prev => prev + 1);
        onError(errorMsg);

        toast({
          title: 'Authentication Failed',
          description: errorMsg,
          status: 'error',
          duration: 5000,
        });
      }
    },
    [requestChallenge, verifyAuth, getHelperData, storedPublicKey, onSuccess, onError, toast, saltData]
  );

  const handleReenroll = useCallback(() => {
    // Clear stored credentials to force re-enrollment
    localStorage.removeItem(ZK_CREDENTIALS_KEY);
    // Notify parent to switch to enrollment mode
    onCancel();
  }, [onCancel]);

  const handleCaptureError = useCallback(
    (errorMsg: string) => {
      setError(errorMsg);
      onError(errorMsg);
    },
    [onError]
  );

  const handleRetry = useCallback(() => {
    setStep('capture');
    setError(null);
    setProgress(0);
  }, []);

  // ============================================================================
  // Render Steps
  // ============================================================================

  const renderCapture = () => (
    <VStack spacing={4} p={4}>
      <VStack spacing={1}>
        <Text fontSize="3xl">🔐</Text>
        <Heading size="sm">Face Login</Heading>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          Hold still - we'll capture {AUTH_SAMPLE_COUNT} samples
        </Text>
      </VStack>

      <FaceCapture
        onCapture={handleFaceCapture}
        onError={handleCaptureError}
        autoCapture={true}
        showGuide={true}
        multiSampleCount={AUTH_SAMPLE_COUNT}
      />

      {error && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Alert>
      )}

      <Button variant="ghost" onClick={onCancel} size="sm">
        Use another method
      </Button>
    </VStack>
  );

  const renderProcessing = () => (
    <VStack spacing={6} p={6} align="center">
      <Spinner size="xl" color="blue.500" thickness="4px" />
      <Heading size="sm">Authenticating...</Heading>
      <Box width="full" px={4}>
        <Progress value={progress} colorScheme="blue" borderRadius="full" size="sm" />
      </Box>
      <Text fontSize="sm" color="gray.400">
        {progress < 30 && 'Requesting challenge...'}
        {progress >= 30 && progress < 60 && 'Deriving your key...'}
        {progress >= 60 && progress < 85 && 'Signing challenge...'}
        {progress >= 85 && 'Verifying...'}
      </Text>
    </VStack>
  );

  const renderSuccess = () => (
    <VStack spacing={4} p={6} align="center">
      <Text fontSize="5xl">✅</Text>
      <Heading size="md" color="green.500">
        Authenticated!
      </Heading>
      <Text color="gray.500" fontSize="sm">
        Logging you in...
      </Text>
    </VStack>
  );

  const renderError = () => {
    const shouldSuggestReenroll = failedAttempts >= MAX_ATTEMPTS_BEFORE_REENROLL;

    return (
      <VStack spacing={4} p={6} align="center">
        <Text fontSize="4xl">🔒</Text>
        <Heading size="sm" color="red.500">
          Authentication Failed
        </Heading>
        <Text color="gray.500" fontSize="sm" textAlign="center">
          {error || 'Face not recognized. Please try again.'}
        </Text>

        {shouldSuggestReenroll && (
          <Alert status="info" borderRadius="md" size="sm">
            <AlertIcon />
            <AlertDescription fontSize="xs">
              Having trouble? Your face may look different from enrollment.
              Try re-enrolling for better accuracy.
            </AlertDescription>
          </Alert>
        )}

        <HStack spacing={3}>
          <Button colorScheme="blue" size="sm" onClick={handleRetry}>
            Try Again
          </Button>
          {shouldSuggestReenroll ? (
            <Button colorScheme="orange" size="sm" onClick={handleReenroll}>
              Re-enroll Face
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Other Methods
            </Button>
          )}
        </HStack>
      </VStack>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      maxW="400px"
      mx="auto"
    >
      {step === 'capture' && renderCapture()}
      {step === 'processing' && renderProcessing()}
      {step === 'success' && renderSuccess()}
      {step === 'error' && renderError()}
    </Box>
  );
};

export default FaceVerification;
